import 'react-quill/dist/quill.snow.css';

import { DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import {
  App,
  Button,
  Col,
  Form,
  Image,
  Modal,
  Row,
  Skeleton,
  Tooltip,
} from 'antd';
import NoImage from 'assets/No_Image_Available.jpg';
import uploadIcon from 'assets/icons/uploadicon.png';
import dayjs from 'dayjs';
import { UpdateEventDetailsDTO } from 'features/events/api/events-api.service';
import { useUpdateEventDetails } from 'features/events/use-cases/event-discription-update';
import { useEventUpdateStatus } from 'features/events/use-cases/event-update-status';
import { useUploadEventGalllary } from 'features/events/use-cases/upload-event-gallary';
import { queryClient } from 'providers';
import { useEffect, useRef, useState } from 'react';
import { payFormatDate, payFormatDateTime } from 'utils/payload-date-';
import { validateExactImageDimensions } from 'utils/validations';

import BasicInfoTextEditor from './basic-info-text-editor/basic-info-text-editor';
import DetailsInformationForm from './details-information-form/details-information-form';

// interface EventDescriptionData {
//   EventDescription: string;
//   EventId: number;
//   EventMasterId: string;
//   EventName: string;
//   EventNameAR: string;
//   CategoryId: number;
//   StartDate: string;
//   EndDate: string;
//   EventDate: string;
//   CurrencyId: number;
//   image_url: string;
//   Address: string,
//   AddressLat: string,
//   AddressLong: string,
//   LocationMapURL: string,
//   Venue: string,
//   MedalPrice: number,
//   TShirtPrice: number,
//   DeliveryPrice: number,
//   PhotoPrice: number,
//   RefundInsurance: boolean,
//   RefundProtectValue: number,
//   EventDescriptionAR: string;
// }

interface props {
  id: string;
  data: any;
  refetch: () => void;
}
const BasicContent = ({ id, data, refetch }: props) => {
  const { isLoading, mutate } = useUpdateEventDetails();
  const [code, setCode] = useState<string>('');
  const [arebicDescr, setArebicDescr] = useState<string>('');
  const [disData, setDisData] = useState<any>('');
  const [discriptionEdit, setDiscriptionEdit] = useState<boolean>(false);
  const [edit, setEdit] = useState<boolean>(false);
  const [mainData, setMainData] = useState<any>();
  localStorage.setItem('categoryName', data.Category);
  const formName = 'event-all-details';

  const { message } = App.useApp();
  const { uploadImage: uploadGallaryImage, loading: isUploading } =
    useUploadEventGalllary();
  const { mutate: updateEventStatus, isLoading: isDeleting } =
    useEventUpdateStatus();
  const bannerFileInputRef = useRef<HTMLInputElement | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string>('');
  const [isVideoPreview, setIsVideoPreview] = useState<boolean>(false);
  const currentBanner =
    bannerPreview || data?.banner_img || data?.banner_image_url || '';

  const isVideoBanner = (url?: string) => {
    if (!url) return false;
    if (url === bannerPreview && isVideoPreview) return true;
    const lower = url.toLowerCase();

    return (
      lower.endsWith('.mp4') ||
      lower.endsWith('.webm') ||
      lower.endsWith('.mov') ||
      lower.includes('.mp4?') ||
      lower.includes('.webm?') ||
      lower.includes('.mov?')
    );
  };

  useEffect(() => {
    if (data?.banner_img || data?.banner_image_url) {
      setBannerPreview('');
      setIsVideoPreview(false);
    }
  }, [data?.banner_img, data?.banner_image_url]);

  const handleBannerUploadClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    bannerFileInputRef.current?.click();
  };

  const handleBannerFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input value to allow selecting the same file again if needed
    e.target.value = '';

    const isWebp = file.type === 'image/webp' || /\.webp$/i.test(file.name);
    if (isWebp) {
      message.error(
        'WebP format is not supported. Please upload a JPEG or PNG image.',
      );

      return;
    }

    const isImage =
      file.type === 'image/jpeg' ||
      file.type === 'image/png' ||
      file.type === 'image/jpg' ||
      /\.(jpe?g|png)$/i.test(file.name);
    const isVideo =
      file.type.startsWith('video/') || /\.(mp4|webm|mov)$/i.test(file.name);

    if (!isImage && !isVideo) {
      message.error(
        'Format not supported. Please upload an image (JPG, PNG) or video (MP4, WebM, MOV).',
      );

      return;
    }

    const objectUrl = URL.createObjectURL(file);

    if (isVideo) {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.src = objectUrl;

      const proceedVideoUpload = () => {
        setIsVideoPreview(true);
        setBannerPreview(objectUrl);

        const formData = new FormData();
        formData.append('files', file, file.name);

        uploadGallaryImage(id, '3', formData, async () => {
          await queryClient.invalidateQueries({
            queryKey: ['getEventDetailsData', id],
          });
          refetch();
        });
      };

      video.onloadedmetadata = () => {
        if (video.videoHeight > video.videoWidth) {
          URL.revokeObjectURL(objectUrl);
          message.error(
            'Portrait videos are not supported for banners. Please upload a landscape video.',
          );

          return;
        }
        proceedVideoUpload();
      };

      video.onerror = () => {
        proceedVideoUpload();
      };

      return;
    }

    const validation = await validateExactImageDimensions(
      file,
      1276,
      449,
      'Event Detail',
    );
    if (!validation.isValid) {
      URL.revokeObjectURL(objectUrl);
      message.error(
        validation.errorMessage ||
          'Please upload an image with dimensions of exactly 1276 × 449 pixels.',
      );

      return;
    }

    setIsVideoPreview(false);
    setBannerPreview(objectUrl);

    const formData = new FormData();
    formData.append('files', file, file.name);

    uploadGallaryImage(id, '3', formData, async () => {
      await queryClient.invalidateQueries({
        queryKey: ['getEventDetailsData', id],
      });
      refetch();
    });
  };

  const handleBannerDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    Modal.confirm({
      title: 'Are you sure you want to delete this banner media?',
      icon: <ExclamationCircleOutlined />,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        const deletedFormData = {
          id:
            data?.banner_image_id ||
            data?.image_id ||
            data?.gallery_id ||
            data?.id ||
            id,
          status: true,
          flag: 'Gallery_Set_Delete',
        };
        updateEventStatus(deletedFormData, {
          onSuccess: () => {
            setBannerPreview('');
            setIsVideoPreview(false);
            setTimeout(async () => {
              await queryClient.invalidateQueries({
                queryKey: ['getEventDetailsData', id],
              });
              refetch();
            }, 500);
          },
        });
      },
    });
  };

  const [form] = Form.useForm();
  const onFinish = (values: any) => {
    const payload = getPayload(values);
    mutate(payload, {
      onSuccess: () => {
        setMainData('');
        refetch();
      },
    });
    setEdit(false);
  };

  const getPayload = (values: any) => {
    const payload: UpdateEventDetailsDTO = {
      ...values,
      EventId: id,
      EventMasterId: id,
      number_seat: values.number_seat ? Number(values.number_seat) : undefined,
      StartDate: payFormatDate(values?.StartDate),
      EndDate: payFormatDate(values?.EndDate),
      EventDate: payFormatDateTime(values?.EventDate),
    };

    return payload;
  };

  function decodeHTMLEntities(text: string) {
    const textArea = document.createElement('textarea');
    textArea.innerHTML = text;

    return textArea.value;
  }

  useEffect(() => {
    setMainData(data);
    form.resetFields();
    setCode(decodeHTMLEntities(data?.EventDescription));
    setDisData(decodeHTMLEntities(data?.EventDescription as any));
    setArebicDescr(data?.EventDescriptionAR as any);
    form.setFieldsValue({
      ...mainData,
      StartDate: dayjs(mainData?.StartDate),
      EndDate: dayjs(mainData?.EndDate),
      EventDate: dayjs(mainData?.EventDate),
    });
    const resjson = JSON.stringify(data);
    localStorage.setItem('event_details', resjson);
  }, [data, mainData]);

  const handleOpenModal = () => {
    setDiscriptionEdit(true);
    document.body.style.overflow = 'hidden';
  };

  const handleCloseModal = () => {
    setDiscriptionEdit(false);
    document.body.style.overflow = 'visible';
  };
  // const rows = 5;
  // const cols = 3;

  // // Create an array to hold the form items
  // const formItems = Array.from({ length: rows * cols }, (_, index) => (
  //   <Col span={8} key={index} style={{ marginBottom: '20px' }}>
  //     <Form.Item name={`field${index + 1}`}>
  //       <Skeleton.Input active style={{ width: "240px" }} />
  //     </Form.Item>
  //   </Col>
  // ));

  const handleEditCancel = () => {
    setMainData('');
    setEdit(false);
    refetch();
  };
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setTimeout(() => setLoading(false), 1000);
    //I mock loading with setTimeout,the skeleton disappears in 5 seconds
  }, []);

  return (
    <>
      {/* {isLoadingGet ? (
        <>

          <Row gutter={16} >
            <Col span={10}>
              <Skeleton.Image style={{ width: 200, height: 200 }} />
            </Col>
            <Col span={12}>
              <Skeleton active paragraph={{ rows: 5 }} />
            </Col>
            <Col span={2}>
              <Skeleton.Button active />
            </Col>
            <Form layout="vertical" style={{ marginTop: "40px" }}>
              {Array.from({ length: rows }, (_, rowIndex) => (
                <Row gutter={30} key={rowIndex}>
                  {formItems.slice(rowIndex * cols, (rowIndex + 1) * cols)}
                </Row>
              ))}
              <Form.Item>
                <Skeleton.Button active />
              </Form.Item>
            </Form>
          </Row>

        </>
      ) : ( */}

      <div className='tabcstmbx'>
        <Form
          // form={form}
          // onFinish={onFinish}
          // layout='vertical'
          id={formName}
          name={formName}
          form={form}
          layout='vertical'
          // initialValues={getInitialValues}
          onFinish={onFinish}
          // requiredMark={'optional'}
          // onFinishFailed={() => {
          //   setValidateTrigger(['onChange']);
          // }}
          // validateTrigger={validateTrigger}
          disabled={isLoading}
        >
          <Row className='edbx'>
            <Col className='cellbx edbx_clm'>
              <div className='edbxcld'>
                <Row>
                  <Col className='edfromedtr' span={24}>
                    <h3>Event Information</h3>

                    <div className='descrip'>
                      {loading ? (
                        <Skeleton.Image
                          active={true}
                          style={{ height: '250px', width: '400px' }}
                          className='skelimg'
                        />
                      ) : (
                        <div
                          className='des_img'
                          style={{
                            backgroundImage:
                              currentBanner && !isVideoBanner(currentBanner)
                                ? `url(${currentBanner})`
                                : undefined,
                            position: 'relative',
                            overflow: 'hidden',
                          }}
                        >
                          {isVideoBanner(currentBanner) ? (
                            <video
                              key={currentBanner}
                              src={currentBanner}
                              autoPlay
                              muted
                              loop
                              playsInline
                              controls
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                borderRadius: '4px',
                              }}
                            />
                          ) : currentBanner ? (
                            <Image
                              src={currentBanner}
                              placeholder={true}
                              preview={true}
                              fallback={NoImage}
                              style={{
                                width: 'auto',
                                height: 'auto',
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                                position: 'absolute',
                                top: 0,
                                bottom: 0,
                                left: 0,
                                right: 0,
                                margin: 'auto',
                              }}
                              wrapperStyle={{
                                width: '100%',
                                height: '100%',
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            />
                          ) : (
                            <img
                              src={NoImage}
                              alt='No Image Available'
                              onError={(e: any) => {
                                e.target.src = NoImage;
                              }}
                            />
                          )}

                          <input
                            type='file'
                            ref={bannerFileInputRef}
                            accept='image/jpeg,image/png,video/mp4,video/webm,video/quicktime'
                            style={{ display: 'none' }}
                            onChange={handleBannerFileChange}
                          />

                          {/* Toggle: Show Upload button when NO media; Show ONLY Delete button when media is present */}
                          {!currentBanner ? (
                            <div
                              style={{
                                position: 'absolute',
                                top: '10px',
                                right: '10px',
                                zIndex: 10,
                              }}
                            >
                              <Tooltip title=' Upload Banner Media Recommended size: 1276 × 449px'>
                                <Button
                                  shape='circle'
                                  type='primary'
                                  icon={
                                    <img
                                      src={uploadIcon}
                                      alt='upload'
                                      style={{
                                        width: '16px',
                                        height: '16px',
                                        filter: 'brightness(0) invert(1)',
                                        objectFit: 'contain',
                                        display: 'block',
                                        margin: 'auto',
                                      }}
                                    />
                                  }
                                  loading={isUploading}
                                  onClick={handleBannerUploadClick}
                                  style={{
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                />
                              </Tooltip>
                            </div>
                          ) : (
                            <div
                              style={{
                                position: 'absolute',
                                top: '10px',
                                right: '10px',
                                zIndex: 10,
                              }}
                            >
                              <Tooltip title='Delete Banner Media'>
                                <Button
                                  shape='circle'
                                  danger
                                  type='primary'
                                  icon={<DeleteOutlined />}
                                  loading={isDeleting}
                                  onClick={handleBannerDeleteClick}
                                  style={{
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                />
                              </Tooltip>
                            </div>
                          )}
                        </div>
                      )}

                      <div className='des_dtls'>
                        <div className='des_d_head'>
                          <h5>Description</h5>
                          <Button onClick={handleOpenModal}>Edit</Button>
                        </div>
                        <div
                          className='des_b'
                          title={
                            disData &&
                            new DOMParser().parseFromString(
                              disData,
                              'text/html',
                            ).documentElement.textContent
                          }
                        >
                          <p dangerouslySetInnerHTML={{ __html: disData }} />
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col span={24}>
                    <div className='edit_info_btns'>
                      <Form.Item className='button-group'>
                        {!edit ? (
                          <Button
                            type='primary'
                            className='btn'
                            onClick={() => setEdit(true)}
                          >
                            Edit
                          </Button>
                        ) : (
                          <>
                            <Button
                              type='primary'
                              className='btn cancel_btn'
                              onClick={handleEditCancel}
                            >
                              Cancel
                            </Button>

                            <Button
                              type='primary'
                              className='btn'
                              htmlType='submit'
                              loading={isLoading}
                            >
                              Update
                            </Button>
                          </>
                        )}
                      </Form.Item>
                    </div>
                    {/* </Form.Item> */}
                    <div className='edfrom'>
                      <DetailsInformationForm
                        edit={edit}
                        form={form}
                        mainData={mainData}
                      />
                    </div>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        </Form>
      </div>

      <div className={`cstmrightmdl ${discriptionEdit && 'show'}`}>
        <BasicInfoTextEditor
          setCode={setCode}
          code={code}
          showModal={discriptionEdit}
          onClose={handleCloseModal}
          id={id}
          fetchEventDiscriptionData={refetch}
          arebicDescr={arebicDescr}
          setArebicDescr={setArebicDescr}
        />
      </div>
    </>
  );
};

export default BasicContent;
